import React, { useState } from 'react';
import { X, Copy, ExternalLink, Check, Sparkles, Layers, Code } from 'lucide-react';
import { GanttTask, GanttConfig } from '../types/gantt';
import { generateAdaptiveCardPayload } from '../utils/adaptiveCardGenerator';

interface LoopModalProps {
  isOpen: boolean;
  onClose: () => void;
  tasks: GanttTask[];
  config: GanttConfig;
  chartDataUrl: string;
}

export const LoopModal: React.FC<LoopModalProps> = ({
  isOpen,
  onClose,
  tasks,
  config,
  chartDataUrl,
}) => {
  const [copiedJson, setCopiedJson] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [viewMode, setViewMode] = useState<'preview' | 'json'>('preview');

  if (!isOpen) return null;

  const cardPayload = generateAdaptiveCardPayload(tasks, config, chartDataUrl);
  const jsonString = JSON.stringify(cardPayload, null, 2);
  const shareableUrl = cardPayload.metadata?.webUrl || 'https://gantt.wdmr.dev';

  const handleCopyJson = async () => {
    try {
      await navigator.clipboard.writeText(jsonString);
      setCopiedJson(true);
      setTimeout(() => setCopiedJson(false), 3000);
    } catch {
      // Fallback
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareableUrl);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 3000);
    } catch {
      // Fallback
    }
  };

  const openInDesigner = () => {
    window.open('https://adaptivecards.io/designer/', '_blank');
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-container" style={{ maxWidth: '680px' }}>
        <div className="modal-header">
          <div className="modal-title">
            <Sparkles size={18} color="#0078D4" />
            <h2>Microsoft Loop Component (Adaptive Card)</h2>
          </div>
          <button onClick={onClose} className="btn-icon">
            <X size={18} />
          </button>
        </div>

        <div className="modal-body">
          <p className="modal-subtitle">
            This Adaptive Card payload renders as a live, collaborative <strong>Microsoft Loop Component</strong> across Microsoft Loop pages, Teams channels, and Outlook emails.
          </p>

          <div className="workspace-tabs" style={{ marginBottom: '8px' }}>
            <button
              className={`tab-btn ${viewMode === 'preview' ? 'active' : ''}`}
              onClick={() => setViewMode('preview')}
            >
              <Layers size={14} />
              <span>Card Overview</span>
            </button>
            <button
              className={`tab-btn ${viewMode === 'json' ? 'active' : ''}`}
              onClick={() => setViewMode('json')}
            >
              <Code size={14} />
              <span>Adaptive Card JSON (v1.5)</span>
            </button>
          </div>

          {viewMode === 'preview' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div
                style={{
                  border: '1px solid #E1DFDD',
                  borderRadius: '8px',
                  backgroundColor: '#FFFFFF',
                  padding: '16px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', borderBottom: '1px solid #EDEBE9', paddingBottom: '10px', marginBottom: '10px' }}>
                  <img src="/favicon.svg" alt="App" width={24} height={24} />
                  <div>
                    <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#201F1E' }}>{config.title || 'Project Roadmap'}</h3>
                    <span style={{ fontSize: '11px', color: '#605E5C' }}>
                      {tasks.length} Activities • Adaptive Card v1.5 Schema
                    </span>
                  </div>
                </div>

                <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '12px' }}>
                  <table className="task-grid" style={{ fontSize: '11px' }}>
                    <thead>
                      <tr>
                        <th>Activity</th>
                        <th>Start</th>
                        <th>End</th>
                        <th>Progress</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tasks.map((t, idx) => (
                        <tr key={idx}>
                          <td><strong>{t.activity}</strong></td>
                          <td>{t.start}</td>
                          <td>{t.end}</td>
                          <td>
                            <span style={{ fontWeight: 600, color: t.start === t.end ? '#0078D4' : (t.progress || 100) === 100 ? '#107C41' : '#D83B01' }}>
                              {t.start === t.end ? 'Milestone ◆' : `${t.progress || 100}%`}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={handleCopyLink} className="btn btn-secondary btn-sm" style={{ flex: 1 }}>
                    {copiedLink ? <Check size={14} color="#107C41" /> : <Copy size={14} />}
                    <span>{copiedLink ? 'Link Copied!' : 'Copy Component Link'}</span>
                  </button>
                  <button onClick={openInDesigner} className="btn btn-secondary btn-sm">
                    <ExternalLink size={14} />
                    <span>Adaptive Cards Designer</span>
                  </button>
                </div>
              </div>

              <div className="help-card" style={{ padding: '10px 14px', fontSize: '12px' }}>
                <strong>How to use in Microsoft Loop & Teams:</strong>
                <ul style={{ paddingLeft: '18px', marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <li><strong>Loop Pages:</strong> Paste the share link into your Loop canvas to unfurl.</li>
                  <li><strong>Power Automate / Bots:</strong> Use the JSON payload with the <em>Post Adaptive Card</em> action.</li>
                </ul>
              </div>
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
              <textarea
                className="paste-textarea"
                style={{ height: '240px', fontSize: '11px' }}
                value={jsonString}
                readOnly
              />
              <button
                onClick={handleCopyJson}
                className="btn btn-primary btn-sm"
                style={{ position: 'absolute', top: '8px', right: '8px' }}
              >
                {copiedJson ? <Check size={13} /> : <Copy size={13} />}
                <span>{copiedJson ? 'Copied JSON!' : 'Copy JSON'}</span>
              </button>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">
            Close
          </button>
          <button onClick={handleCopyJson} className="btn btn-primary">
            {copiedJson ? 'JSON Copied to Clipboard!' : 'Copy Adaptive Card JSON'}
          </button>
        </div>
      </div>
    </div>
  );
};
